import { useLanguageStore } from '@/stores/LanguageStore'

import { PERMISSIONS, UserPermission, allPermissionsList } from './enums/permissions'

export interface IUser {
  description?: string
  name?: string
  email?: string
  password?: string
  clientType?: string
  userType?: UserType
  blocked?: boolean
  updatedAt?: Date
  receiveEmails?: boolean
  isPasswordAlteredOnce?: boolean
}

// export enum UserType {
//   Admin = 'administrator',
//   Operator = 'operator',
//   User = 'user',
//   Erector = 'erector',
//   Manager = 'manager',
//   Sap = 'sap',
// }

export enum UserType {
  Admin = 1,
  Operator = 2,
  User = 3,
  Sap = 4,
  Erector = 5,
  Manager = 6,
  Monitor = 7,
}

export const getUserTypeLabel = (type?: UserType) => {
  const { t } = useLanguageStore()

  switch (type) {
    case UserType.Admin:
      return t('auth.userType.admin')
    case UserType.Operator:
      return t('auth.userType.operator')
    case UserType.User:
      return t('auth.userType.user')
    case UserType.Sap:
      return t('auth.userType.sap')
    case UserType.Erector:
      return t('auth.userType.erector')
    case UserType.Manager:
      return t('auth.userType.manager')
    case UserType.Monitor:
      return t('auth.userType.monitor')
    default:
      return t('auth.userType.invalid')
  }
}

export class IUserDTO implements IUser {
  public description: string
  public name: string
  public email: string
  public password: string
  public clientType?: string
  public userType: UserType
  public blocked: boolean
  public updatedAt: Date
  public receiveEmails?: boolean

  constructor(
    description: string = '',
    name: string = '',
    email: string = '',
    password: string = '',
    clientType: string = '',
    userType: UserType = UserType.User,
    blocked: boolean = false,
    updatedAt: Date = new Date(),
    receiveEmails: false,
  ) {
    this.description = description
    this.name = name
    this.email = email
    this.password = password
    this.clientType = clientType
    this.userType = userType
    this.blocked = blocked
    this.updatedAt = updatedAt
    this.receiveEmails = receiveEmails
  }

  get permissions(): UserPermission[] {
    switch (this.userType) {
      case UserType.Admin:
        return allPermissionsList

      case UserType.Manager:
        return [
          PERMISSIONS.MANAGE.DISPLAY_CONFIG_MANAGE,
          PERMISSIONS.MANAGE.CREATE_MANAGE,
          ...Object.values(PERMISSIONS.PAGES),
          ...Object.values(PERMISSIONS.TABLES),
          ...Object.values(PERMISSIONS.COMMANDS),
        ]

      case UserType.Operator:
      case UserType.Monitor:
        return [
          PERMISSIONS.PAGES.DASHBOARD_VIEW,
          PERMISSIONS.PAGES.METER_VIEW,
          PERMISSIONS.PAGES.SMC_VIEW,
          PERMISSIONS.PAGES.DOWNLOAD_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_GROUPS_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_TAGS_VIEW,
          PERMISSIONS.PAGES.SERVICE_NOTES_VIEW,
          PERMISSIONS.PAGES.PHOTOC_VIEW,
          PERMISSIONS.PAGES.BILLING_VIEW,
          PERMISSIONS.MANAGE.DOWNLOAD_MANAGE,
          PERMISSIONS.TABLES.FROZEN_DATA_VIEW,
          PERMISSIONS.TABLES.MASS_MEMORY_VIEW,
          PERMISSIONS.TABLES.ALARM_VIEW,
          PERMISSIONS.TABLES.EVENT_VIEW,
          PERMISSIONS.TABLES.TICKETS_VIEW,
          PERMISSIONS.TABLES.COMMAND_VIEW,
          PERMISSIONS.TABLES.INSTANT_MEASURE_VIEW,
          PERMISSIONS.TABLES.QEE_VIEW,
          PERMISSIONS.COMMANDS.EXECUTE_COMMANDS,
          PERMISSIONS.COMMANDS.EXECUTE_RELAY_STATE,
          PERMISSIONS.COMMANDS.EXECUTE_UPDATE_DATETIME,
          PERMISSIONS.COMMANDS.EXECUTE_ENERGY_TOTALIZERS,
          PERMISSIONS.COMMANDS.EXECUTE_MASS_MEMORY,
          PERMISSIONS.COMMANDS.EXECUTE_INSTANTANEOUS_READINGS,
          PERMISSIONS.COMMANDS.EXECUTE_QUALITY_PARAMETERS,
          PERMISSIONS.COMMANDS.EXECUTE_GET_RELAY,
          PERMISSIONS.COMMANDS.EXECUTE_CUT_RECONNECT,
          PERMISSIONS.COMMANDS.EXECUTE_SET_RELAY_OFF,
          PERMISSIONS.COMMANDS.EXECUTE_SET_RELAY_ON,
          PERMISSIONS.MANAGE.DISPLAY_CONFIG_MANAGE,
        ]

      case UserType.User:
        return [
          PERMISSIONS.PAGES.METER_VIEW,
          PERMISSIONS.PAGES.SMC_VIEW,
          PERMISSIONS.PAGES.DOWNLOAD_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_GROUPS_VIEW,
          PERMISSIONS.PAGES.EFFECTIVENESSANDAVAILABILITY_VIEW,
          PERMISSIONS.PAGES.QUALITYPARAMETERS_VIEW,
          PERMISSIONS.MANAGE.DOWNLOAD_MANAGE,
          PERMISSIONS.TABLES.FROZEN_DATA_VIEW,
          PERMISSIONS.TABLES.MASS_MEMORY_VIEW,
          PERMISSIONS.TABLES.ALARM_VIEW,
          PERMISSIONS.TABLES.EVENT_VIEW,
          PERMISSIONS.TABLES.TICKETS_VIEW,
          PERMISSIONS.TABLES.COMMAND_VIEW,
        ]
      case UserType.Sap:
        return []
      case UserType.Erector:
        return []

      default:
        return []
    }
  }
}
